import React, { useState } from 'react';
import { Button } from '@rsc/ui-components/shadcn/Button';
import { motion } from 'framer-motion';
import { navigate } from 'gatsby';

import { cn } from '@rsc/tailwind-base/utils';
import Link from '@rsc/ui-components/tailwind/Link';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import useDelayedFunction from '@rsc/ui-components/hooks/useDelayedFunction';
import { getCollectionLink } from '@rsc/utils/getLink';
import GradientImage from './GradientImage';
import menuCollectionSorter from '../../../layout/Header/menuCollectionSorter';

interface Props {
  activeItem: {
    childCollections?: {
      _id?: string;
      store?: {
        title?: string;
      };
    }[];
    collection?: {
      navMoment?: {
        image?: {
          asset?: {
            _id?: string;
            url?: string;
          };
          alt?: string;
          hotspot?: { x: number; y: number };
        };
        link?: string | { linkType: string; href: string };
        headline?: string;
      };
      store?: {
        title?: string;
      };
    };
    ctaText?: string;
    title?: string;
  };
}

function Collection({ activeItem }: Readonly<Props>) {
  const [activeCategory, setActiveCategory] = useState(null);
  const { tt } = useUiComponentsInternationalContext();

  const handleChange = value => {
    setActiveCategory(value);
  };

  const { handleMouseEnter, handleMouseLeave } =
    useDelayedFunction(handleChange);

  let moment = activeCategory?.navMoment;
  if (!moment) {
    moment = activeItem.collection?.navMoment;
  }

  return (
    <div className="cursor-default ps-6 pe-6 md:ps-10 md:pe-10 lg:ps-20 lg:pe-20 2xl:ps-32 2xl:pe-32">
      <div className="collection grid grid-cols-6 lg:grid-cols-12 gap-x-4 md:gap-x-6 gap-y-4 md:gap-y-6">
        <div className="col-span-3">
          <div className="flex flex-col w-full h-full flex-1 overflow-y-scroll pb-6 items-center md:items-start">
            <div className="flex flex-col flex-1 w-full items-start overflow-y-scroll space-y-4 pr-2 pb-4">
              {menuCollectionSorter(
                activeItem.collection?.store?.title,
                activeItem
              ).childCollections?.map(cc => (
                <Link
                  className={cn(
                    'py-[6px] px-3 relative top-[2px] left-[2px] w-full text-left bg-transparent hover:bg-brand-foreground hover:text-secondary-foreground rounded-md transition-all duration-200 ease-in-out text-wrap',
                    activeCategory?._id === cc._id
                      ? 'text-secondary-foreground bg-brand-foreground'
                      : 'text-muted-foreground bg-transparent'
                  )}
                  key={cc._id}
                  link={getCollectionLink(cc.store.title)}
                  onMouseEnter={() => handleMouseEnter(cc)}
                  onMouseLeave={() => handleMouseLeave()}
                  onKeyDown={e => {
                    if (['Enter', 'Space', 'ArrowRight'].includes(e.code)) {
                      e.stopPropagation();
                      e.preventDefault();
                      handleMouseEnter(cc);
                    }
                    if (['Escape'].includes(e.code)) {
                      e.stopPropagation();
                      e.preventDefault();
                      handleMouseLeave();
                    }
                  }}
                >
                  {cc.store.title.split('/').slice(-1)[0]}
                </Link>
              ))}
            </div>
            <div className="flex">
              <Button
                asChild
                className="block whitespace-normal h-auto px-6 py-3"
                rel="canonical"
                variant="primary"
              >
                <Link
                  link={getCollectionLink(activeItem.collection.store.title)}
                  className="text-base font-semibold"
                >
                  {activeItem?.ctaText ||
                    tt(s => s.navBarDeptComponents.shopAllProductsButton, {
                      title: activeItem?.title,
                    })}
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="col-span-3">
          {activeCategory?.childCollections.length > 0 && (
            <div className="flex flex-col items-center md:items-start w-full flex-1">
              <div className="flex flex-col w-full gap-2 items-start overflow-x-hidden">
                {menuCollectionSorter(
                  activeCategory.store.title,
                  activeCategory
                ).childCollections?.map((cc, ix) => (
                  <motion.div
                    key={cc._id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      transition: { delay: ix * 0.05 },
                    }}
                    className="w-full flex"
                  >
                    <Link
                      aria-label={cc.store.title.split('/').slice(-1)[0]}
                      className="flex w-full bg-muted p-4 items-center justify-start rounded-lg"
                      link={getCollectionLink(cc.store.title)}
                      onKeyDown={e => {
                        if (['Enter', 'Space'].includes(e.code)) {
                          e.stopPropagation();
                          e.preventDefault();
                          navigate(getCollectionLink(cc.store.title));
                        }
                      }}
                    >
                      <span className="text-foreground font-bold text-wrap text-left">
                        {cc.store.title.split('/').slice(-1)[0]}
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>

        {moment && (
          <div className="col-span-6">
            <GradientImage
              image={moment?.image}
              link={moment?.link}
              headline={moment?.headline}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Collection;
